"use client";

import Image from "next/image";
import React from "react";

import { Chip, ChipProps } from "@nextui-org/react";
import { useTranslations } from "next-intl";

import { usePropertyOptions } from "@/hooks/usePropertyOptions";
import { cn } from "@/utils/cn";

export interface PropertyStatusBadgeProps {
  /** Property status */
  status: string;
  /** CSS class name for custom styling */
  className?: string;
  /** Badge size */
  size?: ChipProps["size"];
  /** Badge variant */
  variant?: ChipProps["variant"];
  /** Show status icon */
  showIcon?: boolean;
  /** Custom status text override */
  customText?: string;
  /** Additional props for the Chip component */
  chipProps?: Omit<ChipProps, "children" | "color" | "variant" | "size">;
}

/**
 * PropertyStatusBadge - Status badge component for property listings
 *
 * Displays property status with appropriate colors and icons:
 * - Active: Green with checkmark
 * - Inactive: Gray
 * - Pending: Yellow with clock
 * - Sold: Blue with checkmark
 * - Archived: Red with archive icon
 * - Expired: Orange with warning
 * - Promoted: Purple with star
 *
 * @example
 * ```tsx
 * <PropertyStatusBadge
 *   status="active"
 *   size="sm"
 *   showIcon={true}
 * />
 * ```
 */
export const PropertyStatusBadge: React.FC<PropertyStatusBadgeProps> = ({
  status,
  className,
  size = "md",
  variant = "flat",
  showIcon = true,
  customText,
  chipProps = {},
}) => {
  const t = useTranslations("realtor");
  const { getPublicationStatuses } = usePropertyOptions();

  const getStatusConfig = (status: string) => {
    // Get server status data
    const publicationStatuses = getPublicationStatuses();
    const statusOption = publicationStatuses.find((s) => s.key === status);

    const configs: Record<
      string,
      {
        color: ChipProps["color"];
        icon: React.ReactNode;
        text: string;
        ariaLabel: string;
      }
    > = {
      active: {
        color: "success",
        icon: (
          <Image
            src="/assets/icons/ui/status/check-circle.svg"
            alt="Active status"
            width={12}
            height={12}
            className="w-3 h-3"
          />
        ),
        text: t("statusActive"),
        ariaLabel: t("statusActiveAria"),
      },
      inactive: {
        color: "default",
        icon: (
          <Image
            src="/assets/icons/ui/status/x-circle.svg"
            alt="Inactive status"
            width={12}
            height={12}
            className="w-3 h-3"
          />
        ),
        text: t("statusInactive"),
        ariaLabel: t("statusInactiveAria"),
      },
      pending: {
        color: "warning",
        icon: (
          <Image
            src="/assets/icons/ui/status/clock.svg"
            alt="Pending status"
            width={12}
            height={12}
            className="w-3 h-3"
          />
        ),
        text: t("statusPending"),
        ariaLabel: t("statusPendingAria"),
      },
      sold: {
        color: "primary",
        icon: (
          <Image
            src="/assets/icons/ui/status/check-circle.svg"
            alt="Sold status"
            width={12}
            height={12}
            className="w-3 h-3"
          />
        ),
        text: t("statusSold"),
        ariaLabel: t("statusSoldAria"),
      },
      archived: {
        color: "danger",
        icon: (
          <Image
            src="/assets/icons/ui/status/archive.svg"
            alt="Archived status"
            width={12}
            height={12}
            className="w-3 h-3"
          />
        ),
        text: t("statusArchived"),
        ariaLabel: t("statusArchivedAria"),
      },
      expired: {
        color: "warning",
        icon: (
          <Image
            src="/assets/icons/ui/status/warning-triangle.svg"
            alt="Expired status"
            width={12}
            height={12}
            className="w-3 h-3"
          />
        ),
        text: t("statusExpired"),
        ariaLabel: t("statusExpiredAria"),
      },
      promoted: {
        color: "secondary",
        icon: (
          <Image
            src="/assets/icons/ui/status/star.svg"
            alt="Promoted status"
            width={12}
            height={12}
            className="w-3 h-3"
          />
        ),
        text: t("statusPromoted"),
        ariaLabel: t("statusPromotedAria"),
      },
    };

    return (
      configs[status] || {
        color: "default" as ChipProps["color"],
        icon: null,
        text: statusOption?.label || status,
        ariaLabel: `Status: ${statusOption?.label || status}`,
      }
    );
  };

  const config = getStatusConfig(status);

  // Fallback if translation keys are missing
  if (!config) {
    return (
      <Chip
        color="default"
        variant={variant}
        size={size}
        className={cn("font-medium", className)}
        {...chipProps}
      >
        {customText || status}
      </Chip>
    );
  }

  const displayText = customText || config.text || status;

  return (
    <Chip
      color={config.color}
      variant={variant}
      size={size}
      startContent={showIcon ? config.icon : undefined}
      className={cn("font-medium", className)}
      aria-label={config.ariaLabel || `Status: ${displayText}`}
      data-testid={`property-status-${status}`}
      {...chipProps}
    >
      {displayText}
    </Chip>
  );
};

export default PropertyStatusBadge;
